import Markdown from "react-markdown";
import { Box, Heading, Text, useToken } from "@chakra-ui/react";
import { BoxProps } from "@chakra-ui/react";
import { MouseEventHandler, ReactNode, useEffect } from "react";
import ReactDOMServer from "react-dom/server";
import { HashLink } from "react-router-hash-link";
import { useLocation } from "react-router-dom";
import remarkBreaks from "remark-breaks";

interface StandardMarkdownProps extends BoxProps {
  content: string;
  // When true, auto-generates `id` attributes on h1-h4 from their text and
  // scrolls to `location.hash` on mount. Used by long-form docs (policies)
  // that link to their own sections.
  headingAnchors?: boolean;
  // When true, a single line break in the source breaks the line, instead of
  // joining the two lines into one paragraph line as standard markdown does.
  // For short CMS text that editors break by hand (venue arrival info).
  lineBreaks?: boolean;
  // Called when a rendered markdown link is clicked. Applies to both
  // hash-based in-page links and external links.
  onLinkClick?: MouseEventHandler<HTMLAnchorElement>;
}

const getHeaderId = (element: ReactNode): string => {
  const htmlString = ReactDOMServer.renderToStaticMarkup(element);
  return htmlString
    .replace(/<\/?[^>]+(>|$)/g, "")
    .replace(/ /g, "-")
    .toLowerCase();
};

export const StandardMarkdown = ({
  content,
  headingAnchors,
  lineBreaks,
  onLinkClick,
  ...props
}: StandardMarkdownProps) => {
  const [greenDark] = useToken("colors", ["brand.green.dark"]);
  const location = useLocation();

  useEffect(() => {
    if (!headingAnchors || !location.hash) return;
    const el = document.getElementById(location.hash.substring(1));
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  }, [headingAnchors, location.hash]);

  const headingId = (children: ReactNode) =>
    headingAnchors ? getHeaderId(children) : undefined;

  return (
    <Box {...props}>
      <Markdown
        remarkPlugins={lineBreaks ? [remarkBreaks] : undefined}
        components={{
          h1({ children }) {
            return (
              <Heading
                as="h1"
                textStyle="headingLg"
                mb="1rem"
                id={headingId(children)}
              >
                {children}
              </Heading>
            );
          },
          h2({ children }) {
            return (
              <Heading
                as="h2"
                textStyle="headingMd"
                my="1rem"
                id={headingId(children)}
              >
                {children}
              </Heading>
            );
          },
          h3({ children }) {
            return (
              <Heading
                as="h3"
                textStyle="headingSm"
                my="0.75rem"
                id={headingId(children)}
              >
                {children}
              </Heading>
            );
          },
          h4({ children }) {
            return (
              <Heading
                as="h4"
                textStyle="headingSm"
                my="0.75rem"
                fontSize="1rem"
                id={headingId(children)}
              >
                {children}
              </Heading>
            );
          },
          p({ children }) {
            return (
              <Text textStyle="body" mb="0.5rem">
                {children}
              </Text>
            );
          },
          a({ href, children }) {
            const to = href || "";
            const style = {
              color: greenDark,
              textDecoration: "underline",
              fontWeight: 600,
            };
            if (to.startsWith("#")) {
              return (
                <HashLink to={to} onClick={onLinkClick} style={style}>
                  {children}
                </HashLink>
              );
            }
            return (
              <a
                href={to}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onLinkClick}
                style={style}
              >
                {children}
              </a>
            );
          },
          ul({ children }) {
            return (
              <Box
                as="ul"
                lineHeight="1.6"
                pl="1.25rem"
                mb="0.5rem"
                css={{ listStyleType: "disc" }}
              >
                {children}
              </Box>
            );
          },
          ol({ children }) {
            return (
              <Box
                as="ol"
                lineHeight="1.6"
                pl="1.25rem"
                mb="0.5rem"
                css={{ listStyleType: "decimal" }}
              >
                {children}
              </Box>
            );
          },
          li({ children }) {
            return (
              <Box
                as="li"
                color="fg"
                mb="0.25rem"
                css={{ display: "list-item" }}
              >
                {children}
              </Box>
            );
          },
          strong({ children }) {
            return (
              <Text as="span" fontWeight={600}>
                {children}
              </Text>
            );
          },
          em({ children }) {
            return (
              <Text as="span" fontStyle="italic">
                {children}
              </Text>
            );
          },
          hr() {
            return <Box as="hr" my="1.5rem" borderColor="fg" />;
          },
        }}
      >
        {content}
      </Markdown>
    </Box>
  );
};

